import { Box, Stack } from '@mui/material'
import { LabSubscribeButton, LabSubscribersOnlyCard } from '../lab/LabSubscribe.component'

import { AiLabelSection } from '../AiLabel'
import { EditionType } from '../photoStudioTypes'
import { EraserInstructions } from './EraserInstructions.component'
import { GenerateButton } from '../common/GenerateButton'
import { LabPanelShell } from '../lab/LabPanelPrimitives.component'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { NonSubscriberMagicEraserUpsell } from './NonSubscriberMagicEraserUpsell.component'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface MagicEraserPanelProps {
  /** Whether the component is disabled */
  isDisabled?: boolean
  /** Callback to trigger generation */
  onGenerate?: () => void
  /** Whether AI eraser generation has completed */
  hasAIEraserCompleted?: boolean
  /** Whether image is currently processing */
  isProcessingImage?: boolean
  /** Whether a mask currently exists */
  hasMask?: boolean
  /** Whether generation should be enabled based on external logic */
  canGenerate?: boolean
}

/**
 * Magic Eraser Panel component that displays instructions and the AI label settings.
 * Based on Figma design: https://www.figma.com/design/4KaeWeL3snx2flCNKcT65T/Visual-editing?node-id=3817-13432&m=dev
 */
export const MagicEraserPanel: React.FC<MagicEraserPanelProps> = ({
  isDisabled = false,
  onGenerate,
  hasAIEraserCompleted = false,
  isProcessingImage = false,
  hasMask = false,
  canGenerate,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const { aiLabelState, setAiLabelToggle, setAiLabelType, setAiLabelText, setAiLabelColour, setAiLabelOpacity, resetAiLabelPlacement, isLabSdkEngine } = useClientPhotoStudio()

  // Magic Eraser generate button is enabled when the parent controller indicates
  // there is a mask-based change to apply.
  const hasTrigger = typeof canGenerate === 'boolean'
    ? canGenerate
    : hasMask

  // Determine if Generate button should be disabled
  const isGenerateDisabled = isProcessingImage || isDisabled || !hasTrigger

  // Get tooltip for disabled state
  const generateButtonTooltip = (() => {
    if (!hasTrigger && hasAIEraserCompleted) {
      return t('disabled_generate_no_new_mask')
    }
    if (isProcessingImage || isDisabled) {
      return t('disabled_generate_button')
    }
    if (!hasTrigger) {
      return t('disabled_generate_button')
    }
    return undefined
  })()

  return (
    <>
      {/* SUBSCRIBERS ONLY SECTION */}
      {!hasWorkspaceActiveSubscription && !isLabSdkEngine && (
        <NonSubscriberMagicEraserUpsell />
      )}

      {/* Lab engine: the Erase panel itself, locked, like the other Lab tools */}
      {!hasWorkspaceActiveSubscription && isLabSdkEngine && (
        <LabPanelShell footer={<LabSubscribeButton editionType={EditionType.MAGIC_ERASER} />}>

          <LabSubscribersOnlyCard margin="0 0 2.4rem" />

          <EraserInstructions />

        </LabPanelShell>
      )}

      {hasWorkspaceActiveSubscription && (
        <Stack
          sx={{
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: '2.4rem 2.4rem 0 2.4rem',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <Stack
            gap={2.4}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <EraserInstructions />

            {/* AI Label Section — on the Lab engine it sits in the Stage panel instead */}
            {!isLabSdkEngine && (
              <Box sx={{ marginRight: '0.2rem' }}>
                <AiLabelSection
                  showAiLabel={aiLabelState.showAiLabel}
                  onToggle={setAiLabelToggle}
                  labelType={aiLabelState.labelType}
                  onLabelTypeChange={setAiLabelType}
                  labelText={aiLabelState.labelText}
                  onLabelTextChange={setAiLabelText}
                  colour={aiLabelState.colour}
                  onColourChange={setAiLabelColour}
                  opacity={aiLabelState.opacity}
                  onOpacityChange={setAiLabelOpacity}
                  onResetPlacement={resetAiLabelPlacement}
                  isDisabled={isDisabled}
                />
              </Box>
            )}
          </Stack>

          {/* Generate Button - Fixed at bottom */}
          {onGenerate && (
            <GenerateButton
              onGenerate={onGenerate}
              disabled={isGenerateDisabled}
              disabledTooltip={isGenerateDisabled ? generateButtonTooltip : undefined}
              // Lab SDK engine: the same CTA as the Lab Stage / Enhance panels — no "Need something more specific?" help
              helpPlacement={isLabSdkEngine ? 'none' : undefined}
            />
          )}
        </Stack>
      )}
    </>
  )
}
